import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Sparkles, PenLine } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { listBlog, getAdminToken } from "@/lib/api";
import AdSlot from "@/components/AdSlot";
import JsonLd from "@/components/JsonLd";
import { categoryColor } from "@/constants/categoryColors";

const BASE_URL = "https://horseappcompare.com";

const BlogIndexPage = () => {
  const { lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = !!getAdminToken();

  useEffect(() => {
    document.title = lang === "nl" ? "Blog · HorseAppCompare" : "Blog · HorseAppCompare";
    const set = (n, v, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${n}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, n);
        document.head.appendChild(el);
      }
      el.setAttribute("content", v);
    };
    set(
      "description",
      lang === "nl"
        ? "Wekelijkse artikelen, vergelijkingen en koopgidsen over paardenapps en equine software."
        : "Weekly articles, comparisons and buying guides on horse apps and equine software.",
    );
  }, [lang]);

  useEffect(() => {
    setLoading(true);
    listBlog(lang)
      .then(setPosts)
      .finally(() => setLoading(false));
  }, [lang]);

  const isNl = lang === "nl";
  const blogLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    url: `${BASE_URL}/blog`,
    name: "HorseAppCompare Blog",
    description: isNl
      ? "Wekelijkse artikelen, vergelijkingen en koopgidsen over paardenapps en equine software."
      : "Weekly articles, comparisons and buying guides on horse apps and equine software.",
    blogPost: (posts || []).slice(0, 20).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${BASE_URL}/blog/${p.slug}`,
      datePublished: p.published_at || p.created_at,
      inLanguage: p.language || "en",
    })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE_URL}/blog` },
    ],
  };

  return (
    <main>
      <JsonLd id="blog-index" data={blogLd} />
      <JsonLd id="blog-breadcrumb" data={breadcrumbLd} />
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-16 md:py-24 max-w-5xl">
          <div className="overline mb-4 text-[#2563EB]">
            <PenLine size={12} className="inline mr-2" />
            {isNl ? "Blog & Gidsen" : "Blog & Guides"}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-4">
            {isNl ? "Inzichten over paardenapps, wekelijks" : "Insights on horse apps, every week"}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">
            {isNl
              ? "Artikelen, vergelijkingen en koopgidsen op basis van onze database van 25+ paardenapps."
              : "Articles, comparisons and buying guides based on our database of 25+ horse apps."}
          </p>
          {isAdmin && (
            <>
              <Link to="/blog/new" className="hac-btn-secondary mt-6" data-testid="blog-new">
                <Sparkles size={14} /> {isNl ? "Genereer een artikel" : "Generate an article"}
              </Link>
              <Link to="/blog/queue" className="hac-btn-secondary mt-6 ml-3" data-testid="blog-queue-link">
                {isNl ? "Auto-publish queue" : "Auto-publish queue"} →
              </Link>
            </>
          )}
        </div>
      </section>

      <AdSlot placement="blog-banner" />

      <section className="hac-container py-16 md:py-20 max-w-5xl">
        {loading ? (
          <div className="text-center py-20 text-[#94A3B8]">…</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-[#64748B]">
            <p className="mb-4">{isNl ? "Nog geen artikelen." : "No articles yet."}</p>
            {isAdmin && (
              <Link to="/blog/new" className="hac-btn-primary">
                <Sparkles size={14} /> {isNl ? "Genereer artikel" : "Generate article"}
              </Link>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {posts.map((p) => {
              const color = categoryColor(p.category);
              const date = new Date(p.published_at || p.created_at);
              return (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="block bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8 hover:border-[#2563EB] hover:shadow-md transition-all"
                  data-testid={`blog-card-${p.slug}`}
                  style={{ borderTop: `4px solid ${color}` }}
                >
                  <div className="overline mb-3 inline-flex items-center gap-2" style={{ color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                    {p.category || "general"}
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl text-[#0F172A] leading-tight mb-3">
                    {p.title}
                  </h2>
                  <p className="text-sm text-[#64748B] leading-relaxed mb-5 line-clamp-3">
                    {p.hero_lede || p.meta_description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-[#94A3B8]">
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={12} />
                      {date.toLocaleDateString(isNl ? "nl-NL" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </span>
                    <span className="font-mono uppercase">{p.language}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default BlogIndexPage;
